import * as React from "react";
import { useSettingsCache, useRestrictedWords } from "@/shared/cache";
import {
  detectRestrictedWords,
  mergeOverlappingMatches,
} from "@/shared/utils/restrictedWords";
import type { RestrictedWordMatch } from "@/shared/utils/restrictedWords";
import { useIsMobile } from "@/v2/hooks/useIsMobile";
import {
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  TooltipProvider,
} from "@/v2/components/ui/tooltip";

interface HighlightedNarrativeProps {
  description: string;
  matterId?: string;
  client?: string;
  className?: string;
}

function MarkWithTooltip({
  match,
  text,
  isMobile,
}: {
  match: RestrictedWordMatch;
  text: string;
  isMobile: boolean;
}) {
  const markClass =
    match.severity === "block"
      ? "text-inherit rounded-sm px-0.5 bg-red-200/70 dark:bg-red-500/25 underline decoration-wavy decoration-red-500/70"
      : "text-inherit rounded-sm px-0.5 bg-amber-200/70 dark:bg-amber-500/25 underline decoration-wavy decoration-amber-500/70";

  const mark = <mark className={markClass}>{text}</mark>;

  if (isMobile) return mark;

  return (
    <Tooltip>
      <TooltipTrigger asChild>{mark}</TooltipTrigger>
      <TooltipContent>
        <p className="font-medium">{match.displayName}</p>
        {match.suggestion && (
          <p className="text-muted-foreground">{match.suggestion}</p>
        )}
      </TooltipContent>
    </Tooltip>
  );
}

export function HighlightedNarrative({
  description,
  matterId,
  className,
}: HighlightedNarrativeProps) {
  const { settings } = useSettingsCache();
  const { getRestrictedWords } = useRestrictedWords();
  const isMobile = useIsMobile();

  const matches = React.useMemo(() => {
    if (!settings.check_for_red_words || !description) return [];

    const clientCode = matterId?.split(".")[0];
    const matterCode = matterId?.split(".").slice(1).join(".");
    const words = getRestrictedWords(clientCode, matterCode);

    return mergeOverlappingMatches(detectRestrictedWords(description, words));
  }, [settings.check_for_red_words, description, matterId, getRestrictedWords]);

  if (matches.length === 0) {
    return (
      <p className={className}>
        {description}
      </p>
    );
  }

  // Build segments
  const segments: React.ReactNode[] = [];
  let lastIndex = 0;

  for (let i = 0; i < matches.length; i++) {
    const match = matches[i];
    if (match.start > lastIndex) {
      segments.push(description.slice(lastIndex, match.start));
    }
    const matchedText = description.slice(match.start, match.end);
    segments.push(
      <MarkWithTooltip
        key={`m-${i}`}
        match={{ ...match, text: matchedText }}
        text={matchedText}
        isMobile={isMobile}
      />,
    );
    lastIndex = match.end;
  }
  if (lastIndex < description.length) {
    segments.push(description.slice(lastIndex));
  }

  const content = <p className={className}>{segments}</p>;

  if (isMobile) return content;

  return <TooltipProvider>{content}</TooltipProvider>;
}
